/**
 * Data + mutations for the Quiz Editor page.
 *
 * Owns: quiz fetch/refresh, AI "generate more", drag-reorder (optimistic),
 * page-level bulk approve, and the add-dialog open state.
 *
 * Extracted verbatim from QuizEditorPage.jsx during the Phase 3 refactor
 * (no behaviour change).
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { arrayMove } from '@dnd-kit/sortable';
import { useApi } from './useApi';
import { faviconLoader } from '../utils/faviconLoader';

export function useQuizEditor(quizId) {
  const { get, post } = useApi();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [genCount, setGenCount] = useState(4);
  const [generating, setGenerating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await get(`/quizzes/${quizId}`);
      setQuiz(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load quiz');
    }
  }, [get, quizId]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const handleDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = quiz.questions.findIndex(q => q.question_id === active.id);
    const newIdx = quiz.questions.findIndex(q => q.question_id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(quiz.questions, oldIdx, newIdx);
    // Optimistic UI
    setQuiz({ ...quiz, questions: reordered });
    try {
      await post(`/quizzes/${quizId}/questions/reorder`, {
        question_ids: reordered.map(q => q.question_id),
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reorder failed');
      refresh();
    }
  };

  const handleGenerateMore = async () => {
    setGenerating(true);
    const releaseFavicon = faviconLoader.start();
    try {
      await post(`/quizzes/${quizId}/generate`, { num_questions: genCount });
      toast.success(`Generated ${genCount} new questions — review them below`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally {
      releaseFavicon();
      setGenerating(false);
    }
  };

  // Page-level bulk approve. Fires all pending /review calls in parallel
  // (allSettled — one failure shouldn't block the rest). Optimistic refetch
  // at the end picks up server truth.
  const handleApproveAllPending = async (pendingIds) => {
    if (!pendingIds.length) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        pendingIds.map(qid =>
          post(`/quizzes/${quizId}/questions/${qid}/review`, { action: 'approve' })
        )
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      if (failed === 0) toast.success(`Approved ${ok} question${ok === 1 ? '' : 's'}`);
      else toast.warning(`Approved ${ok} of ${results.length} (${failed} failed)`);
      await refresh();
    } finally {
      setBulkBusy(false);
    }
  };

  return {
    quiz,
    loading,
    refresh,
    genCount,
    setGenCount,
    generating,
    handleGenerateMore,
    bulkBusy,
    handleApproveAllPending,
    handleDragEnd,
    addOpen,
    setAddOpen,
  };
}
